import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import { rem } from "polished"
import React, { FC } from "react"
import ReactTooltip, { TooltipProps as ReactTooltipProps } from "react-tooltip"
import styled, { css } from "styled-components"
import { theme } from "@aistihealth/web-common/src/util/styles/theme"

const tooltipMaxWidth = rem(350)
export const tooltipVariants = ["light", "error"] as const

export type TooltipVariant = typeof tooltipVariants[number]

export type TooltipProps = {
  variant?: TooltipVariant
} & Pick<ReactTooltipProps, "id" | "children">

const Container = styled.div<Pick<TooltipProps, "variant">>`
  .__react_component_tooltip {
    ${theme.textStyle("default", "small", "normal")};
    ${theme.shadows.lifted};

    &.show {
      opacity: 1;
    }

    z-index: 1;
    border-radius: ${theme.borderRadius.xsmall};
    padding: ${theme.spacings.small};
    max-width: ${tooltipMaxWidth};

    ${({ variant }) => {
      switch (variant) {
        case "light":
          return css`
            background-color: ${theme.colors.white};
            color: ${theme.colors.black};
          `
        case "error":
          return css`
            background-color: ${theme.colors.redLight};
            color: ${theme.colors.redDark};
          `
        default:
          return assertNever(variant)
      }
    }}

    &.place-top,
    &.place-bottom,
    &.place-left,
    &.place-right {
      &:after {
        ${({ variant }) => {
          switch (variant) {
            case "light":
              return css`
                background-color: ${theme.colors.white};
              `
            case "error":
              return css`
                background-color: ${theme.colors.redLight};
              `
            default:
              return assertNever(variant)
          }
        }}
      }

      &:before {
        height: 4px;
      }
    }
  }
`

export const Tooltip: FC<TooltipProps> = ({ variant, children, ...rest }) => {
  return (
    <Container variant={variant}>
      <ReactTooltip effect="solid" {...rest}>
        {children}
      </ReactTooltip>
    </Container>
  )
}

Tooltip.defaultProps = {
  variant: "light",
}
